/**
 * Calendar.js
 * Calendário semanal/diário limpo e autocontido.
 * Grid de horas × dias com cards de agendamento estilizados.
 */

import React, { useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './Calendar.module.css';
import AppointmentsState from '../../../consts/AppointmentsState';
import { useAttendTourTarget } from '../../../components/guided-tour/GuidedTourContext';
import { SLOT_INTERVAL_MINUTES as SLOT_INTERVAL_MINUTES_DEFAULT } from '../../../consts/CalendarConfig';
import { getNationalHoliday } from '../../../utils/brazilianHolidays';
import { Tooltip } from '@mui/material';
import {
  groupOverlapping,
  fitNonBlockers,
  blockerCardEdges,
  nonBlockerRightOffset,
} from './slotLayout';
import {
  belongsToColumn, buildResourceColumns, columnDayProps, columnResourceId,
  columnHasOwnItems, IDLE_TO_END_MIN_COLUMNS, NARROW_LABEL_MAX, shortResourceLabel, UNASSIGNED_COLUMN_ID,
} from './resourceColumns';
import { offHoursRanges } from '../../../utils/resourceSchedule';
import { columnSlotOpportunity, anyProfessionalSlotOpportunity } from '../v2/hooks/useWaitingListOpportunities';
import AnyProfessionalAxisBadges from './AnyProfessionalAxisBadges';
import DayRoomChip from '../../../components/day-rooms/DayRoomChip';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { AppointmentCard, NonBlockerCard } from './AppointmentCard';
import { DataProviderInstance } from '../../../api/DataProvider';

// ─── Constantes ───────────────────────────────────────────────────────────────
const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTH_ABBR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Largura mínima da coluna de dia (.dayCol no CSS: 80px, 72px no breakpoint de
// tablet) — palpite seguro até o ResizeObserver medir a largura real.
const DAY_COLUMN_MIN_WIDTH = 72;

// A partir de quantas colunas o cabeçalho usa o nome curto (com poucas, a coluna
// é larga e o nome completo cabe)
const SHORT_LABEL_MIN_COLUMNS = 6;
// Largura do eixo de horas (--cv3-time-axis-width): "Localizar profissional" rola a
// coluna para logo depois dele
const TIME_AXIS_WIDTH_PX = 48;
// Tempo que a coluna fica destacada depois de "Localizar profissional"
const HIGHLIGHT_MS = 1500;
// Rolagem horizontal do modo "Profissionais" lembrada entre dias e remontagens
let rememberedResourceScrollLeft = 0;

// Chave de ordem de um card entre os que começam no mesmo horário (ver
// groupOverlapping): posição da agenda na lista da empresa, nome do profissional
// e nome do paciente. Assim a posição lado a lado no Diário é sempre a mesma.
function makeSortKeyOf(resources, patientNameById) {
  const list = resources || [];
  const rank = new Map(list.map((r, i) => [String(r.Id), i]));
  const label = new Map(list.map((r) => [String(r.Id), String(r.Label || '')]));
  return (apt) => {
    const id = apt.resource != null ? String(apt.resource) : '';
    const patient = apt.patient != null ? String(apt.patient) : '';
    return {
      agenda: [rank.has(id) ? rank.get(id) : list.length, label.get(id) || ''],
      name: patientNameById.get(patient) || patient,
    };
  };
}

// Constantes de layout horizontal do slot (desktop):
//   STRIP_PX              faixa do "+" colada à direita — SEMPRE preservada
//   NON_BLOCKER_WIDTH_PX  largura da faixa de reagendado/cancelado/falta
//   MIN_BLOCKER_WIDTH_PX  largura mínima legível de um card bloqueante
const SLOT_LAYOUT_DESKTOP = {
  STRIP_PX: 16,
  NON_BLOCKER_WIDTH_PX: 28,
  MIN_BLOCKER_WIDTH_PX: 40,
  GAP_PX: 2,
};

// ─── Formata hora "HH:MM" ─────────────────────────────────────────────────────
function formatTime(date) {
  const d = new Date(date);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// Altura mínima de um card. No compacto (48px por hora) um atendimento de 20 min
// tem 16px e o card fica nessa altura exata, senão invadiria o card de baixo; o
// AppointmentCard vira uma linha só (cardTiny). No confortável 20 min ≈ 27px.
const MIN_CARD_HEIGHT_PX = { comfortable: 28, compact: 16 };

// ─── Calcula geometria (top + height) de um agendamento ──────────────────────
function calcCardGeometry(appointment, startHour, slotHeight, slotIntervalMinutes, density) {
  const date = new Date(appointment.date);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const topPx = (hours - startHour + minutes / 60) * slotHeight;

  let heightPx = slotHeight * (slotIntervalMinutes / 60); // padrão = intervalo configurado
  if (appointment.duration) {
    const [dh, dm] = appointment.duration.split(':').map(Number);
    const durationHours = dh + dm / 60;
    heightPx = Math.max(durationHours * slotHeight, MIN_CARD_HEIGHT_PX[density] || MIN_CARD_HEIGHT_PX.comfortable);
  }
  return { topPx, heightPx };
}

// ─── Indicador de hora atual ──────────────────────────────────────────────────
const CurrentTimeIndicator = ({ startHour, endHour, slotHeight }) => {
  const [topPx, setTopPx] = useState(0);
  const [timeLabel, setTimeLabel] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      const currentTotalMinutes = h * 60 + m;
      const startTotalMinutes = startHour * 60;
      const endTotalMinutes = endHour * 60;
      setIsVisible(currentTotalMinutes >= startTotalMinutes && currentTotalMinutes <= endTotalMinutes);
      setTopPx((h - startHour + m / 60) * slotHeight);
      setTimeLabel(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [startHour, endHour, slotHeight]);

  if (!isVisible) return null;

  return (
    <div className={styles.currentTimeLine} style={{ top: topPx }}>
      <span className={styles.currentTimeLabel}>{timeLabel}</span>
    </div>
  );
};

// ─── Coluna de um dia ─────────────────────────────────────────────────────────
// resourceLabel/resourceId: no modo "Profissionais" a coluna é de um profissional
// (mesmo dia em todas); o cabeçalho mostra o nome e o clique leva a agenda dele.
// resourceRoom: sala do dia da agenda (chip sob o nome, abre o popup de sala do dia).
// hideResource: o card não repete o nome do profissional (a coluna já diz de quem é).
// highlighted: destaque passageiro depois de "Localizar profissional".
// narrow: colunas estreitas (100px) escolhidas pela pessoa no modo "Profissionais":
// cabeçalho mais curto e, no Compacto, card sem horário (o eixo da esquerda já diz).
// sortKeyOf: ordem dos cards lado a lado no mesmo horário (makeSortKeyOf).
const DayColumn = ({ date, appointments, allDayAppointments, startHour, endHour, onAppointmentSelect, today, slotHeight, density, primaryMonth, isNonWorkingDay, blockedHours, slotIntervalMinutes, activeSlot, slotOpportunities, resourceLabel, resourceId, resourceRoom, ownHours = null, hideResource = false, highlighted = false, narrow = false, shortLabel = false, sortKeyOf = null }) => {
  const isResourceColumn = resourceLabel !== undefined;
  const isToday = date.toDateString() === today.toDateString();
  const isOtherMonth = primaryMonth !== undefined && date.getMonth() !== primaryMonth;
  const tourTarget = useAttendTourTarget();
  const hours = [];
  for (let h = startHour; h < endHour; h++) hours.push(h);

  // Agrupa agendamentos sobrepostos para calcular largura/posição
  const grouped = useMemo(() => groupOverlapping(appointments, sortKeyOf), [appointments, sortKeyOf]);

  // ── Largura real da coluna ────────────────────────────────────────────────
  // Medida com ResizeObserver (fallback: getBoundingClientRect no mount + resize
  // da janela). Até medir, assume o min-width para que a primeira pintura já
  // seja segura. A largura alimenta fitNonBlockers, que decide quantas faixas
  // de non-blocker cabem sem espremer os cards bloqueantes.
  const dayBodyRef = useRef(null);
  const [columnWidthPx, setColumnWidthPx] = useState(DAY_COLUMN_MIN_WIDTH);

  useEffect(() => {
    const el = dayBodyRef.current;
    if (!el) return undefined;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect && rect.width > 0) setColumnWidthPx(rect.width);
    };
    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    return undefined;
  }, []);

  // Decide quantas faixas de non-blocker cabem de fato na largura medida
  const laidOut = useMemo(
    () => fitNonBlockers(grouped, columnWidthPx, SLOT_LAYOUT_DESKTOP),
    [grouped, columnWidthPx]
  );

  // Conta agendamentos reais do dia usando a lista bruta (sem filtros aplicados).
  // Exclui: disponível, bloqueado, reagendado, cancelado, cancelado pelo paciente e falta (no show).
  const countSource = allDayAppointments || appointments;
  const appointmentCount = countSource.filter(
    a => a.status !== AppointmentsState.Available &&
         a.status !== AppointmentsState.Blocked &&
         a.status !== AppointmentsState.Rescheduled &&
         a.status !== AppointmentsState.Canceled &&
         a.status !== AppointmentsState.CanceledByPatient &&
         a.status !== AppointmentsState.Missed
  ).length;

  // Slot strip hover — chave "h-m" do slot com mouse sobre ele
  const [hoveredStrip, setHoveredStrip] = useState(null);
  // Sobre um card o "+" não vale: o card tem a própria dica
  const [hoverOnCard, setHoverOnCard] = useState(false);

  function calcTimeFromY(rect, clientY) {
    const offsetY = clientY - rect.top;
    const totalMinutes = (offsetY / slotHeight) * 60;
    const hour = startHour + Math.floor(totalMinutes / 60);
    const minute = Math.floor((totalMinutes % 60) / slotIntervalMinutes) * slotIntervalMinutes;
    return { hour, minute, offsetY };
  }

  function handleBodyMouseMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const { hour, minute } = calcTimeFromY(rect, e.clientY);

    // Calcula qual strip de slot está sob o cursor para aplicar o hover visual
    // (os strips têm pointer-events:none, então usamos o mousemove do dayBody)
    const stripKey = `${hour}-${minute}`;
    setHoveredStrip(stripKey);
    setHoverOnCard(!!e.target.closest('[data-card]'));
  }

  function handleBodyMouseLeave() {
    setHoveredStrip(null);
    setHoverOnCard(false);
  }

  // Nome do "+" do horário (os strips têm pointer-events:none, então a dica fica no dayBody)
  const hoveredStripTitle = (() => {
    if (!hoveredStrip || hoverOnCard) return undefined;
    const [h, m] = hoveredStrip.split('-').map((n) => String(n).padStart(2, '0'));
    return `Novo agendamento às ${h}:${m}`;
  })();

  // Clique em área vazia → abre popup de novo agendamento com hora calculada
  function handleBodyClick(e) {
    if (!onAppointmentSelect) return;
    // Ignora se o clique foi em cima de um card existente
    if (e.target.closest('[data-card]')) return;

    const bodyRect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - bodyRect.top;
    const totalMinutes = (offsetY / slotHeight) * 60;
    const clickedHour = startHour + Math.floor(totalMinutes / 60);
    const clickedMinute = Math.floor(totalMinutes % 60 / slotIntervalMinutes) * slotIntervalMinutes;

    const clickedDate = new Date(date);
    clickedDate.setHours(clickedHour, clickedMinute, 0, 0);

    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${clickedDate.getFullYear()}-${pad(clickedDate.getMonth() + 1)}-${pad(clickedDate.getDate())} ${pad(clickedDate.getHours())}:${pad(clickedDate.getMinutes())}:00`;

    // Calcula a posição do slot clicado para ancorar o popover
    const slotTopInBody = (clickedHour - startHour + clickedMinute / 60) * slotHeight;
    const slotRect = {
      left: bodyRect.left,
      top: bodyRect.top + slotTopInBody,
      right: bodyRect.right,
      bottom: bodyRect.top + slotTopInBody + slotHeight * (slotIntervalMinutes / 60),
      width: bodyRect.width,
      height: slotHeight * (slotIntervalMinutes / 60),
    };

    // Passa evento enriquecido com _slotRect para o pai ancorar o popover ao slot
    const anchoredEvent = { ...e, clientX: slotRect.left, clientY: slotRect.top, _slotRect: slotRect };
    const slotData = { status: AppointmentsState.Available, date: dateStr };
    if (isResourceColumn && resourceId != null) slotData.resource = resourceId;
    onAppointmentSelect(slotData, anchoredEvent);
  }

  // Feriado nacional: só aviso no cabeçalho, a grade continua livre para marcar
  const holiday = getNationalHoliday(date);

  // Cabeçalho da coluna do profissional: nome curto quando há muitas colunas (o
  // completo fica na dica); a contagem só aparece quando há atendimento
  // Coluna estreita: sempre o nome curto, com limite menor ("Jessica B."); o nome
  // completo fica na dica do mouse
  const headerLabel = !isResourceColumn ? null
    : narrow ? shortResourceLabel(resourceLabel, NARROW_LABEL_MAX)
    : shortLabel ? shortResourceLabel(resourceLabel)
    : resourceLabel;
  const countBadge = appointmentCount > 0 && (
    <div className={styles.dayHeaderCount}>
      <EventOutlinedIcon sx={{ fontSize: 9 }} />
      <span>{appointmentCount}</span>
    </div>
  );

  return (
    <div
      className={`${styles.dayCol} ${isResourceColumn ? styles.dayColResource : ''} ${isResourceColumn && narrow ? styles.dayColResourceNarrow : ''} ${highlighted ? styles.dayColHighlight : ''} ${isToday ? styles.dayColToday : ''} ${isOtherMonth ? styles.dayColOtherMonth : ''}`}
      data-resource-col={isResourceColumn && resourceId != null ? resourceId : undefined}
    >
      {/* Cabeçalho do dia — número + nome em linha + contador abaixo */}
      <div
        className={`${styles.dayHeader} ${isResourceColumn ? styles.dayHeaderResource : isToday ? styles.dayHeaderToday : ''} ${isOtherMonth ? styles.dayHeaderOtherMonth : ''} ${holiday && !isToday ? styles.dayHeaderHoliday : ''}`}
      >
        {/* Selo de feriado no canto com o nome (truncado se faltar espaço); o popup
            mostra o nome completo ao passar o mouse ou tocar */}
        {holiday && (
          <Tooltip title={`Feriado: ${holiday}`} arrow placement="bottom-start" enterTouchDelay={0} leaveTouchDelay={3000}>
            <span className={styles.dayHeaderHolidayBadge} tabIndex={0}>{holiday}</span>
          </Tooltip>
        )}
        {isResourceColumn ? (
          <>
            <Tooltip title={headerLabel === resourceLabel ? '' : resourceLabel} arrow placement="bottom" enterDelay={400}>
              <span className={styles.dayHeaderResourceName}>{headerLabel}</span>
            </Tooltip>
            {resourceRoom && (
              <div className={styles.dayHeaderResourceRow}>
                <DayRoomChip summary={resourceRoom} compact readOnly />
                {countBadge}
              </div>
            )}
          </>
        ) : (
        <div className={styles.dayHeaderTop}>
          <span className={`${styles.dayHeaderNum} ${isToday ? styles.dayHeaderNumToday : ''}`}>
            {date.getDate()}
          </span>
          <div className={styles.dayHeaderNameWrap}>
            <span className={styles.dayHeaderName}>{DAY_NAMES[date.getDay()]}</span>
            {isOtherMonth && (
              <span className={styles.dayHeaderMonthTag}>{MONTH_ABBR[date.getMonth()]}</span>
            )}
          </div>
        </div>
        )}
        {!(isResourceColumn && resourceRoom) && countBadge}
      </div>

      {/* Grade de horas */}
      <div
        ref={dayBodyRef}
        className={`${styles.dayBody}${isNonWorkingDay ? ` ${styles.dayBodyNonWorking}` : ''}`}
        style={{ height: (endHour - startHour) * slotHeight }}
        onClick={handleBodyClick}
        title={hoveredStripTitle}
        onMouseMove={handleBodyMouseMove}
        onMouseLeave={handleBodyMouseLeave}
      >
        {/* Linhas de hora inteira */}
        {hours.map(h => (
          <React.Fragment key={h}>
            <div
              className={styles.hourLine}
              style={{ top: (h - startHour) * slotHeight }}
            />
          </React.Fragment>
        ))}

        {/* Overlays de slot disponível — apenas em dias úteis */}
        {!isNonWorkingDay && hours.map(h => (
          <div
            key={`avail-${h}`}
            className={styles.slotAvailable}
            style={{
              top: (h - startHour) * slotHeight + 2,
              height: slotHeight - 4,
            }}
          />
        ))}

        {/* Profissional com horário próprio: listra o que fica fora das faixas dele */}
        {!isNonWorkingDay && ownHours && offHoursRanges(ownHours, startHour, endHour).map(([from, to]) => (
          <div
            key={`offhours-${from}`}
            className={styles.slotBlocked}
            style={{
              top: ((from - startHour * 60) / 60) * slotHeight + 2,
              height: Math.max(0, ((to - from) / 60) * slotHeight - 4),
            }}
          />
        ))}

        {/* Overlays de horários bloqueados pela empresa (BlockedTimes) — apenas em dias úteis */}
        {!isNonWorkingDay && (blockedHours || []).map(hour => {
          if (hour < startHour || hour >= endHour) return null;
          return (
            <div
              key={`blockedtime-${hour}`}
              className={styles.slotBlocked}
              style={{
                top: (hour - startHour) * slotHeight + 2,
                height: slotHeight - 4,
              }}
            />
          );
        })}

        {/* Cards de agendamento */}
        {laidOut.map((item) => {
          const {
            appointment, colIndex, widthPct, leftPct,
            nonBlockerAlone, nonBlockerOverlap, nbIndex, nbVisible, nbBadgeCount,
          } = item;
          const { topPx, heightPx } = calcCardGeometry(appointment, startHour, slotHeight, slotIntervalMinutes, density);
          // STRIP_PX: faixa direita SEMPRE reservada para o "+" (área de clique para novo agendamento)
          const STRIP_PX = SLOT_LAYOUT_DESKTOP.STRIP_PX;
          const isGhost = appointment.id === '__ghost__';

          if (isGhost) {
            const startTime = formatTime(appointment.date);
            return (
              <div
                key="__ghost__"
                className={styles.cardWrapperGhost}
                style={{
                  top: topPx,
                  height: heightPx,
                  width: `calc(${widthPct}% - ${STRIP_PX + 4}px)`,
                  left: `calc(${leftPct}% + 2px)`,
                }}
              >
                <div className={styles.ghostCard}>
                  <span className={styles.ghostCardTime}>{startTime}</span>
                  <span className={styles.ghostCardLabel}>Bloqueado</span>
                </div>
              </div>
            );
          }

          // ── Non-blocker (sozinho no slot ou sobreposto): faixa fina à direita,
          // antes do STRIP do "+". Empilha por prioridade: nbIndex 0 é o mais
          // importante (falta) e fica colado no "+"; reagendado é o primeiro a cair.
          if (nonBlockerAlone || nonBlockerOverlap) {
            // Não coube depois do "+" e dos bloqueantes na largura mínima:
            // não renderiza (a contagem vai no badge "+N" da última faixa visível).
            if (!nbVisible) return null;
            const rightOffset = nonBlockerRightOffset(nbIndex, SLOT_LAYOUT_DESKTOP);
            return (
              <div
                key={`${appointment.id || `${appointment.date}-nb-${nbIndex}`}`}
                className={styles.cardWrapper}
                data-card="true"
                onMouseMove={e => e.stopPropagation()}
                onMouseEnter={e => { e.stopPropagation(); setHoveredStrip(null); }}
                style={{
                  top: topPx,
                  height: heightPx,
                  width: `${SLOT_LAYOUT_DESKTOP.NON_BLOCKER_WIDTH_PX}px`,
                  right: `${rightOffset}px`,
                  left: 'auto',
                }}
              >
                <NonBlockerCard
                  appointment={appointment}
                  heightPx={heightPx}
                  onClick={onAppointmentSelect}
                  badgeCount={nbBadgeCount}
                />
              </div>
            );
          }

          // ── Agendamento bloqueante: divide em partes iguais a área da coluna
          // MENOS rightReserved (faixa do "+" + faixas de non-blocker que couberam).
          // Sempre renderiza e nunca invade a faixa do "+".
          const edges = blockerCardEdges(item, SLOT_LAYOUT_DESKTOP.GAP_PX);

          return (
            <div
              key={`${appointment.id || `${appointment.date}-${colIndex}`}`}
              className={styles.cardWrapper}
              data-card="true"
              data-tour={tourTarget(appointment)}
              onMouseMove={e => e.stopPropagation()}
              onMouseEnter={e => { e.stopPropagation(); setHoveredStrip(null); }}
              style={{
                top: topPx,
                height: heightPx,
                left: edges.left,
                right: edges.right,
                width: 'auto',
              }}
            >
              <AppointmentCard
                appointment={appointment}
                heightPx={heightPx}
                onClick={onAppointmentSelect}
                density={density}
                hideResource={hideResource}
                hideTime={narrow}
              />
            </div>
          );
        })}

        {/* Slots de meia hora — overlay visual de hover apenas (pointer-events: none) */}
        {/* O hover é calculado pelo onMouseMove do dayBody; o clique pelo handleBodyClick */}
        {hours.flatMap(h => {
          const slotsPerHour = Math.floor(60 / slotIntervalMinutes);
          return Array.from({ length: slotsPerHour }, (_, idx) => {
            const m = idx * slotIntervalMinutes;
            const stripKey = `${h}-${m}`;
            const slotTopPx = (h - startHour) * slotHeight + (m / 60) * slotHeight;
            const slotHeightPx = slotHeight * (slotIntervalMinutes / 60);
            const isHovered = hoveredStrip === stripKey;
            const pad = (n) => String(n).padStart(2, '0');
            const timeLabel = `${pad(h)}:${pad(m)}`;

            // Verifica se este strip corresponde ao slot ativo (popover aberto)
            const isActive = activeSlot
              && activeSlot.date instanceof Date
              && activeSlot.date.getFullYear() === date.getFullYear()
              && activeSlot.date.getMonth() === date.getMonth()
              && activeSlot.date.getDate() === date.getDate()
              && activeSlot.hour === h
              && activeSlot.minute === m
              && (!isResourceColumn || activeSlot.resourceId === undefined
                || String(activeSlot.resourceId) === String(resourceId));

            // Verifica se há oportunidade da lista de espera para este slot
            // Não exibe em dias não-úteis nem em horários bloqueados pela empresa
            const isBlockedHour = (blockedHours || []).includes(h);
            const dateStr = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
            // Coluna de profissional: só quem pode ser atendido por ele e com a coluna livre
            const opportunity = !isNonWorkingDay && !isBlockedHour
              ? columnSlotOpportunity(slotOpportunities, { isResourceColumn, resourceId, appointments, date, dateStr, hour: h, minute: m, slotIntervalMinutes })
              : null;
            const hasOpportunity = !!opportunity;
            const isHighOpportunity = hasOpportunity && opportunity.avgScore >= 65;

            return (
              <div
                key={`strip-${h}-${m}`}
                data-slot={stripKey}
                data-time={timeLabel}
                className={[
                  styles.slotAddStrip,
                  isActive ? styles.slotAddStripActive : '',
                  !isActive && isHovered ? styles.slotAddStripHovered : '',
                ].filter(Boolean).join(' ')}
                style={{ top: slotTopPx, height: slotHeightPx, right: 0, left: 0, width: '100%' }}
              >
                {/* Selo da lista de espera — sempre visível quando há quem queira o horário.
                    Mesmo ícone do menu "Lista de espera" e a cor roxa do indicador da agenda */}
                {hasOpportunity && !isActive && (
                  <div
                    className={[
                      styles.slotOpportunityBadge,
                      isHighOpportunity ? styles.slotOpportunityBadgeHigh : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <HourglassEmptyOutlinedIcon className={styles.slotOpportunityIcon} />
                    <span className={styles.slotOpportunityText}>
                      {opportunity.patientCount === 1
                        ? '1 na lista de espera'
                        : `${opportunity.patientCount} na lista de espera`}
                    </span>
                  </div>
                )}
              </div>
            );
          });
        })}

        {/* Indicador de hora atual (só no dia de hoje, e só se a hora atual estiver no intervalo visível) */}
        {isToday && <CurrentTimeIndicator startHour={startHour} endHour={endHour} slotHeight={slotHeight} />}
      </div>
    </div>
  );
};

// ─── Coluna de horários (eixo Y) ──────────────────────────────────────────────
// anyBadges: props do AnyProfessionalAxisBadges (modo "Profissionais"), ou null
const TimeAxis = ({ startHour, endHour, slotHeight, anyBadges = null }) => {
  const hours = [];
  for (let h = startHour; h < endHour; h++) hours.push(h);

  return (
    <div className={styles.timeAxis}>
      <div className={styles.timeAxisHeader} />
      <div className={styles.timeAxisBody} style={{ height: (endHour - startHour) * slotHeight }}>
        {hours.map(h => (
          <div
            key={h}
            className={styles.timeAxisSlot}
            style={{ top: (h - startHour) * slotHeight }}
          >
            <span className={styles.timeAxisLabel}>
              {String(h).padStart(2, '0')}:00
            </span>
          </div>
        ))}
        {anyBadges && (
          <AnyProfessionalAxisBadges startHour={startHour} endHour={endHour} slotHeight={slotHeight} {...anyBadges} />
        )}
      </div>
    </div>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
// resourceColumns ([{ id, label, room? }]): modo "Profissionais" — uma coluna por
// profissional para o único dia em `days`. Sem ela, uma coluna por dia (como sempre).
// `room` é a sala do dia da agenda (chip só leitura no cabeçalho).
// resourceSchedules ({ [id]: Schedule }): horário próprio de cada agenda; a coluna
// do profissional (ou a do dia, com focusedResourceId) segue as faixas dele.
// narrowColumns: colunas de 100px no modo "Profissionais" (escolha da pessoa).
// focusColumnId + focusColumnNonce: "Localizar profissional" — a cada nonce novo rola até
// a coluna do profissional, expande se estiver recolhida e a destaca por instantes.
const Calendar = ({
  days = [],
  appointments = [],
  allAppointments = null,
  startHour = 8,
  endHour = 20,
  onAppointmentSelect,
  showOutsideWorkingHours = false,
  slotHeight = 80,
  density = 'comfortable',
  primaryMonth,
  availableDaysOfWeek = null,
  blockedTimes = [],
  slotIntervalMinutes = SLOT_INTERVAL_MINUTES_DEFAULT,
  activeSlot = null,
  slotOpportunities = null,
  resourceColumns = null,
  resourceSchedules = null,
  focusedResourceId = null,
  companyHours = null,
  narrowColumns = false,
  focusColumnId = null,
  focusColumnNonce = 0,
}) => {
  const displayStart = showOutsideWorkingHours ? 0 : startHour;
  const displayEnd = showOutsideWorkingHours ? 24 : endHour;
  const isResourceMode = Boolean(resourceColumns);
  const dayKey = days[0] ? days[0].toDateString() : null;
  const daysAreaRef = useRef(null);

  // Ordem dos cards lado a lado no mesmo horário: lista de agendas da empresa e
  // nomes (a lista de pacientes é estável; GetPatientById muda a cada render)
  const { CompanyConfig, patients } = DataProviderInstance();
  const resources = CompanyConfig ? CompanyConfig.Resources : null;
  const patientNameById = useMemo(
    () => new Map((patients || []).map((p) => [String(p.id), p.name || ''])),
    [patients],
  );
  const sortKeyOf = useMemo(() => makeSortKeyOf(resources, patientNameById), [resources, patientNameById]);

  // Memória da rolagem horizontal: ao trocar de dia (ou voltar para a tela) a
  // pessoa continua nas mesmas colunas em vez de cair na primeira agenda
  const handleAreaScroll = () => {
    const area = daysAreaRef.current;
    if (!isResourceMode || !area) return;
    // Enquanto a área ainda não comporta a posição lembrada (dia carregando,
    // colunas mudando) o navegador encosta a rolagem no limite menor: isso não
    // é vontade da pessoa, então não sobrescreve a posição lembrada
    const maxScroll = area.scrollWidth - area.clientWidth;
    if (area.scrollLeft < rememberedResourceScrollLeft && maxScroll < rememberedResourceScrollLeft) return;
    rememberedResourceScrollLeft = area.scrollLeft;
  };
  useLayoutEffect(() => {
    const area = daysAreaRef.current;
    if (!area || !isResourceMode) return;
    if (area.scrollLeft !== rememberedResourceScrollLeft) area.scrollLeft = rememberedResourceScrollLeft;
    // Roda de novo quando os dados do dia chegam (antes disso a área pode nem
    // comportar a posição lembrada)
  }, [dayKey, isResourceMode, appointments, allAppointments]);

  // "Localizar profissional": rola até a coluna e destaca por instantes. O nonce muda
  // a cada pedido, mesmo para a mesma agenda.
  const [highlightedId, setHighlightedId] = useState(null);
  const pendingFocusRef = useRef(0);
  useEffect(() => {
    if (!focusColumnNonce || focusColumnId == null) return undefined;
    pendingFocusRef.current = focusColumnNonce;
    setHighlightedId(focusColumnId);
    const timer = setTimeout(() => setHighlightedId(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusColumnNonce]);
  useLayoutEffect(() => {
    if (!pendingFocusRef.current || focusColumnId == null) return;
    const area = daysAreaRef.current;
    const col = area && area.querySelector(`[data-resource-col="${focusColumnId}"]`);
    if (!col) return;
    pendingFocusRef.current = 0;
    const left = col.getBoundingClientRect().left - area.getBoundingClientRect().left + area.scrollLeft - TIME_AXIS_WIDTH_PX;
    area.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  });

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const dayPropsFor = (id, day) => columnDayProps({ resourceSchedules, availableDaysOfWeek, blockedTimes, companyHours }, id, day);

  // Lista bruta para contagem (sem filtros de status aplicados)
  const rawAppointments = allAppointments || appointments;

  const getAppointmentsForDay = (day) =>
    appointments.filter(apt => {
      const d = new Date(apt.date);
      return (
        d.getDate() === day.getDate() &&
        d.getMonth() === day.getMonth() &&
        d.getFullYear() === day.getFullYear()
      );
    });

  const getAllAppointmentsForDay = (day) =>
    rawAppointments.filter(apt => {
      const d = new Date(apt.date);
      return (
        d.getDate() === day.getDate() &&
        d.getMonth() === day.getMonth() &&
        d.getFullYear() === day.getFullYear()
      );
    });

  // Modo "Profissionais": "Qualquer profissional" da lista de espera é um selo só
  // por horário no eixo de horas, com as agendas visíveis livres no horário
  const anyBadges = (() => {
    if (!resourceColumns || !days[0] || !slotOpportunities || slotOpportunities.size === 0) return null;
    const day = days[0];
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
    const columnIds = resourceColumns.map((c) => c.id);
    const dayAppointments = getAppointmentsForDay(day);
    return {
      date: day,
      slotIntervalMinutes,
      onSelect: onAppointmentSelect,
      opportunityAt: (hour, minute) => anyProfessionalSlotOpportunity(slotOpportunities, {
        columnIds, appointments: dayAppointments, date: day, dateStr, hour, minute, slotIntervalMinutes,
      }),
    };
  })();

  return (
    <div className={styles.calendarRoot} data-tour="agenda-week">
      {/* Scroll container único — timeAxis sticky à esquerda + colunas de dias */}
      <div className={styles.daysArea} ref={daysAreaRef} data-days-area="true" onScroll={handleAreaScroll}>
        {/* Eixo de horas — sticky left dentro do scroll */}
        <TimeAxis startHour={displayStart} endHour={displayEnd} slotHeight={slotHeight} anyBadges={anyBadges} />

        {/* Modo "Profissionais": colunas de profissionais do mesmo dia */}
        {resourceColumns && days[0] && (() => {
          const day = days[0];
          const dayAppointments = getAppointmentsForDay(day);
          const allDayAppointments = getAllAppointmentsForDay(day);
          const columns = buildResourceColumns(resourceColumns, dayAppointments);
          // Com muitas agendas, as sem nada no dia vão para o fim da fila, na
          // largura normal; "Sem profissional" fica onde está
          const isIdle = (column) => column.id !== UNASSIGNED_COLUMN_ID && !columnHasOwnItems(allDayAppointments, column.id);
          const ordered = columns.length > IDLE_TO_END_MIN_COLUMNS
            ? [...columns.filter((c) => !isIdle(c)), ...columns.filter(isIdle)]
            : columns;
          return ordered.map((column) => (
            <DayColumn
              key={`resource-${column.id}`}
              date={day}
              appointments={dayAppointments.filter((apt) => belongsToColumn(apt, column.id))}
              allDayAppointments={allDayAppointments.filter((apt) => belongsToColumn(apt, column.id))}
              startHour={displayStart}
              endHour={displayEnd}
              onAppointmentSelect={onAppointmentSelect}
              today={today}
              slotHeight={slotHeight}
              density={density}
              {...dayPropsFor(columnResourceId(column), day)}
              slotIntervalMinutes={slotIntervalMinutes}
              activeSlot={activeSlot}
              slotOpportunities={slotOpportunities}
              resourceLabel={column.label}
              resourceId={columnResourceId(column)}
              resourceRoom={column.room}
              hideResource={column.id !== UNASSIGNED_COLUMN_ID}
              highlighted={highlightedId != null && String(column.id) === String(highlightedId)}
              narrow={narrowColumns}
              shortLabel={columns.length > SHORT_LABEL_MIN_COLUMNS}
              sortKeyOf={sortKeyOf}
            />
          ));
        })()}

        {/* Colunas de dias */}
        {!resourceColumns && days.map(day => {
          return (
            <DayColumn
              key={day.toISOString()}
              date={day}
              appointments={getAppointmentsForDay(day)}
              allDayAppointments={getAllAppointmentsForDay(day)}
              startHour={displayStart}
              endHour={displayEnd}
              onAppointmentSelect={onAppointmentSelect}
              today={today}
              slotHeight={slotHeight}
              density={density}
              primaryMonth={primaryMonth}
              {...dayPropsFor(focusedResourceId, day)}
              slotIntervalMinutes={slotIntervalMinutes}
              activeSlot={activeSlot}
              slotOpportunities={slotOpportunities}
              hideResource={focusedResourceId != null}
              sortKeyOf={sortKeyOf}
            />
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
